import { useApp } from '@/contexts';

import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';

interface WizardEnvironmentStepProps {
  sourceType: string;
  destinationType: string;
  onNext: (
    sourceEnv: string,
    destinationEnv: string,
    sourceConnection: string,
    destinationConnection: string
  ) => void;
  initialSource?: string;
  initialDestination?: string;
  initialSourceConnection?: string;
  initialDestinationConnection?: string;
}

export const WizardEnvironmentStep = ({
  sourceType,
  destinationType,
  onNext,
  initialSource = '',
  initialDestination = '',
  initialSourceConnection = '',
  initialDestinationConnection = '',
}: WizardEnvironmentStepProps) => {
  const { appId } = useApp();
  const [sourceEnv, setSourceEnv] = useState(initialSource);
  const [destinationEnv, setDestinationEnv] = useState(initialDestination);
  const [sourceConnection, setSourceConnection] = useState(initialSourceConnection);
  const [destinationConnection, setDestinationConnection] = useState(initialDestinationConnection);

  const { data: environments = [] } = useGetEnvironmentsQuery(appId);

  // Filter environments that have the required connector types
  const sourceEnvs = environments.filter((env) =>
    env.credentials?.some((c) => c.credentialType === sourceType)
  );

  const destinationEnvs = environments.filter((env) =>
    env.credentials?.some((c) => c.credentialType === destinationType)
  );

  // Get available connections for selected environments
  const sourceConnections =
    environments
      .find((e) => e.id === sourceEnv)
      ?.credentials?.filter((c) => c.credentialType === sourceType) || [];

  const destinationConnections =
    environments
      .find((e) => e.id === destinationEnv)
      ?.credentials?.filter((c) => c.credentialType === destinationType) || [];

  // Auto-select connection if only one exists
  if (sourceEnv && sourceConnections.length === 1 && !sourceConnection) {
    setSourceConnection(sourceConnections[0].key);
  }

  if (destinationEnv && destinationConnections.length === 1 && !destinationConnection) {
    setDestinationConnection(destinationConnections[0].key);
  }

  const handleNext = () => {
    if (sourceEnv && destinationEnv && sourceConnection && destinationConnection) {
      onNext(sourceEnv, destinationEnv, sourceConnection, destinationConnection);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Environments
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which environments to use for source and destination
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Source Environment</InputLabel>
                <Select
                  value={sourceEnv}
                  onChange={(e) => {
                    setSourceEnv(e.target.value);
                    setSourceConnection(''); // Reset connection on env change
                  }}
                  label="Source Environment"
                >
                  {sourceEnvs.map((env) => (
                    <MenuItem key={env.id} value={env.id}>
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {sourceEnv && sourceConnections.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Source Connection</InputLabel>
                  <Select
                    value={sourceConnection}
                    onChange={(e) => setSourceConnection(e.target.value)}
                    label="Source Connection"
                  >
                    {sourceConnections.map((conn) => (
                      <MenuItem key={conn.key} value={conn.key}>
                        {conn.connectionName || conn.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Destination Environment</InputLabel>
                <Select
                  value={destinationEnv}
                  onChange={(e) => {
                    setDestinationEnv(e.target.value);
                    setDestinationConnection(''); // Reset connection on env change
                  }}
                  label="Destination Environment"
                >
                  {destinationEnvs.map((env) => (
                    <MenuItem key={env.id} value={env.id}>
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {destinationEnv && destinationConnections.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Destination Connection</InputLabel>
                  <Select
                    value={destinationConnection}
                    onChange={(e) => setDestinationConnection(e.target.value)}
                    label="Destination Connection"
                  >
                    {destinationConnections.map((conn) => (
                      <MenuItem key={conn.key} value={conn.key}>
                        {conn.connectionName || conn.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!sourceEnv || !destinationEnv || !sourceConnection || !destinationConnection}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
